import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import useAudioPlayer from '@/shared/hooks/useAudioPlayer';
import { DEFAULT_SPEECH_LOCALE } from '@/shared/config/ui';
import * as playButtonActions from '@/shared/modules/playButton';

interface UseWordAudioProps {
	currentWord: string;
	shouldAutoPlay?: boolean;
}

interface UseWordAudioReturn {
	handlePlayWord: (wordToPlay: string, forcePlay?: boolean) => void;
	isWordAudioPlaying: boolean;
}

/**
 * Custom hook to manage word audio playback for Letter Party game
 *
 * Handles:
 * - Requesting audio from the API for the current word
 * - Playing audio automatically when it becomes available
 * - Manual playback triggering with validation
 * - Pending audio state tracking
 * - Redux state management for audio
 */
export function useWordAudio({ currentWord, shouldAutoPlay = true }: UseWordAudioProps): UseWordAudioReturn {
	const dispatch = useDispatch();

	// Select audio state from Redux with shallow equality check to prevent unnecessary re-renders
	const { song, songId, isSongLoading, requestedWord } = useSelector((state: any) => {
		const { song, songId, loading, requestData } = state.playButtonReducer || {};
		const normalizedSongId = songId ?? null;
		const isLetterPartySong = normalizedSongId === 'letter-party';
		const normalizedRequestedWord =
			typeof requestData?.word === 'string' ? requestData.word.trim().toUpperCase() : null;

		return {
			song: isLetterPartySong ? song || null : null,
			songId: normalizedSongId,
			isSongLoading: isLetterPartySong ? Boolean(loading) : false,
			requestedWord: isLetterPartySong ? normalizedRequestedWord : null,
		};
	}, shallowEqual);

	const getSong = useCallback(
		(id: string, word: string, lang: string) => {
			dispatch(playButtonActions.getSong(id, word, lang));
		},
		[dispatch],
	);

	// Setup audio player with current song URL
	const { play: playWordAudio, stop: stopWordAudio } = useAudioPlayer({
		src: song?.url || undefined,
		volume: 1,
		html5: true,
		respectGlobalAudioSetting: false,
	});

	// Track pending audio requests to prevent duplicate plays
	const pendingWordAudioRef = useRef<string | null>(null);
	const previousWordRef = useRef<string>('');
	const [isWordAudioPlaying, setIsWordAudioPlaying] = useState(false);
	const audioTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Helper function to play audio with state tracking
	const playWithTracking = useCallback(() => {
		// Clear any existing timeout
		if (audioTimeoutRef.current) {
			clearTimeout(audioTimeoutRef.current);
		}

		// Set playing state
		setIsWordAudioPlaying(true);

		// Play the audio
		playWordAudio();

		// Set timeout to clear playing state after audio duration
		// Default to 2 seconds if duration not available
		const duration = song?.duration ? song.duration * 1000 : 2000;
		audioTimeoutRef.current = setTimeout(() => {
			setIsWordAudioPlaying(false);
			audioTimeoutRef.current = null;
		}, duration);
	}, [playWordAudio, song?.duration]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (audioTimeoutRef.current) {
				clearTimeout(audioTimeoutRef.current);
			}
		};
	}, []);

	// Request audio for the current word
	useEffect(() => {
		const normalizedWord = currentWord.trim().toUpperCase();
		if (!normalizedWord) {
			pendingWordAudioRef.current = null;
			previousWordRef.current = '';
			return;
		}

		// Stop previous audio if word changed
		if (previousWordRef.current && previousWordRef.current !== normalizedWord) {
			stopWordAudio();
		}

		previousWordRef.current = normalizedWord;
		pendingWordAudioRef.current = normalizedWord;
		getSong('letter-party', currentWord, DEFAULT_SPEECH_LOCALE);
	}, [currentWord, getSong, stopWordAudio]);

	// Auto-play audio when it becomes ready for the pending word
	useEffect(() => {
		if (!shouldAutoPlay) {
			return;
		}

		const normalizedWord = currentWord.trim().toUpperCase();

		if (!normalizedWord) {
			return;
		}

		// Only play when the latest request matches this word
		if (!requestedWord || requestedWord !== normalizedWord) {
			return;
		}

		// Ensure this is still the pending word
		if (pendingWordAudioRef.current !== normalizedWord) {
			return;
		}

		// Wait for audio to finish loading
		if (isSongLoading) {
			return;
		}

		// Ensure audio URL is available
		if (!song?.url) {
			return;
		}

		// Verify this is audio for letter-party game
		if (songId !== 'letter-party') {
			return;
		}

		// Play the audio with tracking and clear pending state
		stopWordAudio();
		playWithTracking();
		pendingWordAudioRef.current = null;
	}, [shouldAutoPlay, currentWord, isSongLoading, playWithTracking, requestedWord, song?.url, songId, stopWordAudio]);

	/**
	 * Manually trigger word playback with validation
	 */
	const handlePlayWord = useCallback(
		(wordToPlay: string, forcePlay: boolean = false) => {
			const normalizedCurrentWord = currentWord.trim().toUpperCase();
			if (!normalizedCurrentWord) {
				return;
			}

			const normalizedWord = wordToPlay.trim().toUpperCase();

			// Only play if the word matches the current word
			if (!normalizedWord || normalizedWord !== normalizedCurrentWord) {
				return;
			}

			// If audio is already loaded and ready, play it
			if (songId === 'letter-party' && song?.url && !isSongLoading && requestedWord === normalizedCurrentWord) {
				if (forcePlay) {
					stopWordAudio();
				}
				playWithTracking();
				pendingWordAudioRef.current = null;
				return;
			}

			// Otherwise, request the audio and mark it as pending
			pendingWordAudioRef.current = normalizedCurrentWord;
			getSong('letter-party', currentWord, DEFAULT_SPEECH_LOCALE);
		},
		[currentWord, getSong, isSongLoading, playWithTracking, requestedWord, song?.url, songId, stopWordAudio],
	);

	return {
		handlePlayWord,
		isWordAudioPlaying,
	};
}
